import PropTypes from "prop-types";
import event_fallback from "../assets/fallback/artist_splash_fallback.webp";
import Badge from "./Badge";

const optimizeExternalImage = (url) => {
  try {
    const path = url.replace(
      "https://s3.cruncho.co/eventmanager-assets/public/",
      "",
    );
    return `https://ik.imagekit.io/cruncho/${path}?tr=f-webp,w-600,q-50`;
  } catch {
    return url;
  }
};

const EventCard = ({ event }) => {
  const altText = "Evenemangsbild för " + event.name;
  const eventUrl = `/events/${event.slug}`;

  const imageSrc = event.image_main
    ? event.image_main
    : event.external_source_image
      ? optimizeExternalImage(event.external_source_image)
      : event_fallback;

  // Format date to a more readable format
  const formatDate = (dateString) => {
    if (!dateString) return "Datum saknas";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Ogiltigt datum";
    return date.toLocaleDateString("sv-SE", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };
  if (event) {
    return (
      <div className="group w-full max-w-sm bg-[#131313] shadow-lg rounded-2xl border border-white/10 overflow-hidden flex flex-col relative transition-all duration-300 hover:scale-[1.02] hover:border-white/20">
        {/* Event type badge */}
        <div className="absolute top-3 right-3 z-10">
          <Badge text="Evenemang" type="event" />
        </div>
        {/* Image section */}
        <a
          href={eventUrl}
          className="block w-full h-52 shrink-0 overflow-hidden relative"
        >
          <img
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            src={imageSrc}
            alt={altText}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#131313] via-transparent to-transparent opacity-50" />
        </a>
        {/* Content section */}
        <div className="flex flex-col flex-grow p-6 space-y-4">
          <div>
            <a href={eventUrl} className="block">
              <h4 className="font-extrabold text-xl text-white uppercase tracking-wide leading-snug hover:text-[#0047ff] transition-colors line-clamp-2">
                {event.name}
              </h4>
            </a>
          </div>
          <div className="flex flex-col gap-3 flex-grow">
            <div className="flex items-start gap-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 text-[#0047ff] flex-shrink-0 mt-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <div>
                <p className="text-[10px] text-stone-400 font-bold uppercase tracking-wider">
                  Datum
                </p>
                <p className="text-sm text-stone-300 font-medium">{formatDate(event.date)}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 text-[#0047ff] flex-shrink-0 mt-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              <div>
                <p className="text-[10px] text-stone-400 font-bold uppercase tracking-wider">
                  Plats
                </p>
                <p className="text-sm text-stone-300 font-medium">{event.location ?? "Plats okänd"}</p>
              </div>
            </div>
          </div>
          <div className="mt-auto pt-2">
            <a
              href={eventUrl}
              className="btn w-full bg-[#0047ff] hover:bg-blue-700 text-white border-none rounded-full font-bold uppercase tracking-wider text-xs py-3.5 transition-transform duration-300 hover:scale-[1.03]"
              aria-label={`Läs mer om ${event.name}`}
            >
              Läs mer
            </a>
          </div>
        </div>
      </div>
    );
  }
};

EventCard.propTypes = {
  event: PropTypes.shape({
    name: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired,
    image_main: PropTypes.string,
    external_source_image: PropTypes.string,
    date: PropTypes.string.isRequired,
    location: PropTypes.string.isRequired,
  }).isRequired,
};

export default EventCard;
